import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { ThemePalette } from '@angular/material/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LifestyleRewardsService } from 'src/app/services/lifestyle-rewards/lifestyle-rewards.service';
import { InitiateLifestyleRewardsResponse } from 'src/app/interfaces/lifestyle-rewards/InitiateLifestyleRewardsResponse';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Session } from 'src/app/interfaces/session-data';
import { HttpErrorResponse } from '@angular/common/http';
import { DirtyErrorStateMatcherDirective } from 'src/app/directives/dirty-error-state-matcher.directive';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { SessionService } from 'src/app/services/session/session.service';

@Component({
  selector: 'app-lifestyle-rewards-login-version-two',
  templateUrl: './lifestyle-rewards-login.component.html',
  styleUrls: ['./lifestyle-rewards-login.component.scss']
})
export class LifestyleRewardsLoginComponent implements OnInit {
  private baseUrl: string;

  public color: ThemePalette = 'primary';
  public diameter = 50;

  matcher = new DirtyErrorStateMatcherDirective();

  loginForm: FormGroup;
  public mobileNumber: string;
  public pin: string;

  mobile;
  showErrors = true;
  session: Session;

  formErrors = {
    mobileNumber: '',
    pin: '',
  };

  validationMessages = {
    mobileNumber: {
      required: 'Mobile number is required',
      pattern: 'Mobile number must be 10 digits starting with 0'
    },
    pin: {
      required: 'PIN is required',
      minLength: 'PIN must be at least 4 characters long'
    }
  };

  public hide = true;
  public loading = false;

  @Output()
  notify: EventEmitter<{}> = new EventEmitter<{ undefined }>();

  constructor(
    private router: Router,
    private lifestyleRewardsService: LifestyleRewardsService,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private _snackBar: MatSnackBar,
    private breakpointObserver: BreakpointObserver,
    private sessionService: SessionService
  ) {
    this.baseUrl = this.sessionService.getVirtualTwoBaseurl();
    this.createForm();
    breakpointObserver.observe([
      Breakpoints.HandsetLandscape,
      Breakpoints.HandsetPortrait
    ]).subscribe(result => {
      this.mobile = result.matches;
    });
  }

  ngOnInit(): void {
    this.session = JSON.parse(sessionStorage.getItem('sessionData'));
  }

  getColorScheme(): string {
    if (this.session && this.session.sessionDisplayInformation && this.session.sessionDisplayInformation.colorScheme) {
      return this.session.sessionDisplayInformation.colorScheme.replace(';', '');
    }
    return 'inherit';
  }

  getTextColor(): string {
    if (this.session && this.session.sessionDisplayInformation && this.session.sessionDisplayInformation.colorScheme) {
      let bgColor: string = this.session.sessionDisplayInformation.colorScheme.replace(';', '');
      let color = bgColor.charAt(0) === '#' ? bgColor.substring(1, 7) : bgColor;
      let r = parseInt(color.substring(0, 2), 16);
      let g = parseInt(color.substring(2, 4), 16);
      let b = parseInt(color.substring(4, 6), 16);
      let luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5 ? '#000000' : '#FFFFFF';
    }
    return 'inherit';
  }

  get mobileNumberControl() {
    return this.loginForm.get('mobileNumber');
  }

  get pinControl() {
    return this.loginForm.get('pin');
  }

  openSnackBar(message: string, action: string) {
    this._snackBar.open(message, action, {
      duration: 5000,
    });
  }

  resetValidation() {
    sessionStorage.setItem('formSubmitted', 'false');
  }

  formValidation() {
    if (this.mobileNumberControl && this.mobileNumberControl.value) {
      this.showErrors = false;
      this.makeFieldsDirty();
      if (this.loginForm.valid) {
        this.initiateLifestyleRewards();
      }
    } else {
      this.mobileNumberControl.setErrors(Validators.required);
      this.showErrors = true;
      return;
    }
  }

  createForm() {
    this.loginForm = this.fb.group({
      mobileNumber: ['', [Validators.required, Validators.pattern(/^0\d{9}$/)]],
      pin: ['', [Validators.required, Validators.minLength(4)]],
    });
  }

  makeFieldsDirty() {
    Object.keys(this.loginForm.controls)
      .forEach(field => {
        const control = this.loginForm.get(field);
        control.markAsDirty({ onlySelf: true });
        control.markAsTouched({ onlySelf: true });
      });
  }

  initiateLifestyleRewards() {
    if (this.loginForm.valid) {
      sessionStorage.setItem('formSubmitted', 'true');
      this.loading = true;
      this.makeFieldsDirty();
      this.lifestyleRewardsService.initiateTransaction(
        sessionStorage.getItem('sessionToken'),
        this.mobileNumberControl.value,
        this.pinControl.value
      ).subscribe((data: InitiateLifestyleRewardsResponse) => {
        sessionStorage.setItem('lifestyle-rewards-data', JSON.stringify(data));
        this.loading = false;
        this.router.navigate([`${this.baseUrl}/transaction-status`]);
      }, (err: HttpErrorResponse) => {
        if (err.status === 401) {
          this.openSnackBar('Invalid Mobile Number/PIN combination', 'Try Again');
          this.showErrors = true;
          this.loading = false;
        } else if (err.status === 400) {
          this.openSnackBar(err.error?.message || 'Invalid request', 'Try Again');
          this.showErrors = true;
          this.loading = false;
        } else {
          this.router.navigate([`${this.baseUrl}/transaction-status`]);
        }
      });
    } else {
      if (this.loginForm.controls.mobileNumber.valid === false || this.loginForm.controls.mobileNumber.touched === false) {
        this.loginForm.controls.mobileNumber.setValue('');
      }
      if (this.loginForm.controls.pin.valid === false || this.loginForm.controls.pin.touched === false) {
        this.loginForm.controls.pin.setValue('');
      }
    }
  }

  cancelTransaction() {
    this.router.navigate([`${this.baseUrl}/transaction-status/userCancelled`]);
  }
}
